import { apolloClient } from '@/main';
import store from '@/store';

import UpdateArtwork from '@/mutations/UpdateArtwork';
import UpdateArtworkNameDescription from '@/mutations/UpdateArtworkNameDescription'
import PublishArtwork from '@/mutations/PublishArtwork';
import UnpublishArtwork from '@/mutations/UnpublishArtwork';

/**
 * @description Cleans files array of unwanted properties (__typename in this case)
 * @param {Object[]} filesArray - artwork.files with unwanted properties
 * @returns {Object[]} Array without unwanted properties
 */
export function cleanFilesArray(filesArray) {
  if (Array.isArray(filesArray)) {
    return filesArray.map((file) => {
      delete file.__typename;
      return file;
    });
  }
  return null;
}
/**
 * @typedef ArtworkModel
 * @property {String} id
 * @property {String} [name]
 * @property {String} [sceneConfig]
 * @property {String} [creatorVersion]
 * @property {Boolean} [animationHasAlpha]
 * @property {Boolean} [animationVideoOrientation]
 */


/**
 * @description Performs an UpdateArtworkInput mutation and updates the vuex state, rolls-back vuex state changes in case of error
 * @param {String} artworkId
 * @param {{id: String|undefined, status=String|undefined, name=String|undefined, description=String|undefined, userid=String|undefined, sceneConfig=String|undefined}} modifiedFields
 * @returns
 */
export function updateArtworkDetails(artworkId, modifiedFields) {
  return new Promise((res, rej) => {
    console.log(`ArtworkServices: updateArtworkDetails() on fields: ${Object.keys(modifiedFields)}`);
    const artwork = store.getters.artworkById(artworkId);
    // Store old artwork to revert back to incase of errors
    const oldArtwork = Object.assign({}, artwork);
    const newArtwork = Object.assign({}, artwork);
    // Assign the modifications to newArtwork
    Object.assign(newArtwork, modifiedFields);
    // Create the apollo payload if a variable doesn't exist, pass as null
    const payload = {
      id: artworkId,
      userid: store.getters.userId,
      status: newArtwork.status ? newArtwork.status : null,
      name: newArtwork.name ? newArtwork.name : null,
      description: newArtwork.description ? newArtwork.description : null,
    };
    console.log(payload);
    apolloClient.mutate({
      mutation: UpdateArtworkNameDescription,
      variables: {
        input: payload,
      },
      update: (proxy, { data: { updateArtwork } } ) => {
        store.commit('UPDATE_ARTWORK', {
          artwork: newArtwork
        });
        console.log(`UpdateArtworkDetails: Applied update to vuex store after ${updateArtwork.optimisticResponse ? 'optimistic' : 'server'} response.`);
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateArtwork: {
          __typename: 'Artwork',
          ...payload,
          isOptimistic: true,
        }
      }
    }).catch(error => {
      console.error('ArtworkServices: updateArtwork ', error);
      // Revert changes
      store.commit('UPDATE_ARTWORK', {
        artwork: oldArtwork
      });
      rej(error);
    }).then((result => res(result)));
  });
}
/**
 * @description Performs an UpdateArtwork mutation and updates the vuex state, rolls-back vuex state changes in case of error
 * @param {String} artworkId
 * @param {{files: Array|*, name: String|*, sceneConfig: String|*, creatorVersion: String|*, animationHasAlpha: Boolean|*, animationVideoOrientation: String|*}} modifiedFields
 * @returns
 */
export function updateArtwork(artworkId, modifiedFields) {
  return new Promise((res, rej) => {
    console.log(`ArtworkServices: updateArtwork() on fields: ${Object.keys(modifiedFields)}`);
    const artwork = store.getters.artworkById(artworkId);
    // Store old artwork to revert back to incase of errors
    const oldArtwork = Object.assign({}, artwork);
    const newArtwork = Object.assign({}, artwork);
    // Assign the modifications to newArtwork
    Object.assign(newArtwork, modifiedFields);
    // Create the apollo payload if a variable doesn't exist, pass as null
    const payload = {
      id: artworkId,
      status: newArtwork.status ? newArtwork.status : null,
      files: newArtwork.files ? cleanFilesArray(newArtwork.files) : null,
      sceneConfig: newArtwork.sceneConfig ? newArtwork.sceneConfig : null,
      creatorVersion: newArtwork.creatorVersion ? newArtwork.creatorVersion : null,
      animationHasAlpha: newArtwork.animationHasAlpha ? newArtwork.animationHasAlpha : null,
      animationVideoOrientation: newArtwork.animationVideoOrientation ? newArtwork.animationVideoOrientation : null,
      modified: Date.now(),
    };
    console.log(payload);
    apolloClient.mutate({
      mutation: UpdateArtwork,
      variables: {
        input: payload,
      },
      update: (proxy, { data: { updateArtwork } } ) => {
        store.commit('UPDATE_ARTWORK', {
          artwork: newArtwork
        });
        console.log(`UpdateArtwork: Applied update to vuex store after ${updateArtwork.optimisticResponse ? 'optimistic' : 'server'} response.`);
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateArtwork: {
          __typename: 'Artwork',
          ...payload,
          optimisticResponse: true,
        }
      }
    }).catch(error => {
      console.error('ArtworkServices: updateArtwork ', error);
      // Revert changes
      store.commit('UPDATE_ARTWORK', {
        artwork: oldArtwork
      });
      rej(error);
    }).then((result => res(result)));
  });
}

/**
 *
 * @desc Removes files from artwork and performs appsync mutation + updates vuex state.
 * @param {String} artworkId
 * @param {String[]} fileIdsToDelete
 * @returns {Promise<unknown>}
 */
export function deleteFilesFromArtwork(artworkId, fileIdsToDelete) {
  console.log('ArtworkServices: Deleting ${fileIdsToDelete} from ${artworkId} artwork.');
  const artwork = store.getters.artworkById(artworkId);
  const files = artwork.files.filter(
    file => !fileIdsToDelete.some(fileID => file.fileID === fileID));

  return updateArtwork(artworkId, { files });
}

/**
 * @description Adds files to the 'files' field on an artwork.
 * @param {String} artworkId
 * @param {Object[]} newFiles
 */
export function addFilesToArtwork(artworkId, newFiles) {
  console.log('ArtworkServices: Adding ${newFiles.length} to ${artworkId} artwork.');
  const artwork = store.getters.artworkById(artworkId);
  if (!Array.isArray(newFiles)) {
    console.error('Error: addAssetsToArtwork(): Parameter newFiles is not an array. (type: ' + typeof(newFiles) + ')');
  }
  // Add newFiles to existing files
  const files = [...artwork.files, ...newFiles];
  // Update artwork
  return updateArtwork(artworkId, { files });
}
/**
 *
 * @desc Performs a PublishArtwork or UnpublishArtwork mutation (depending on 'status' parametre) and updates vuex.
 * @export
 * @param {String} artworkId
 * @param {'published'|'draft'} status
 * @returns
 */
export function updateArtworkStatus(artworkId, status) {
  console.log(`ArtworkServices: Changing status to ${status} on ${artworkId} artwork.`);
  return new Promise((resolve, reject) => {
    const artwork = store.getters.artworkById(artworkId);
    const oldArtwork = Object.assign({}, artwork);
    const newArtwork = Object.assign({}, artwork);
    newArtwork.status = status;
    newArtwork.modified = Date.now();
    const mutation = status === 'published' ? PublishArtwork : UnpublishArtwork;
    apolloClient.mutate({
      mutation,
      variables: {
        id: artwork.id,
      },
      update: () => {
        store.commit('UPDATE_ARTWORK', {
          artwork: newArtwork
        });
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateArtwork: {
          __typename: 'Artwork',
          id: artwork.id
        }
      }
    })
      .then(() => {
        resolve(status);
      })
      .catch(error => {
        console.error(error);
        reject(error);
        store.commit('UPDATE_ARTWORK', {
          artwork: oldArtwork
        });
      });
  });
}

/**
 * @desc Sets the per-artwork redirectLaunchV3 flag (launch-v3 redirect toggle) via an UpdateArtwork
 *       mutation and updates vuex; rolls back on error. true = redirect the legacy page to the
 *       launch-v4 WebAR experience; false/absent (default) = keep the legacy native-app launch page.
 * @export
 * @param {String} artworkId
 * @param {Boolean} redirectLaunchV3
 * @returns {Promise<Boolean>}
 */
export function updateArtworkLaunchMode(artworkId, redirectLaunchV3) {
  console.log(`ArtworkServices: Setting redirectLaunchV3=${redirectLaunchV3} on ${artworkId} artwork.`);
  return new Promise((resolve, reject) => {
    const artwork = store.getters.artworkById(artworkId);
    const oldArtwork = Object.assign({}, artwork);
    const newArtwork = Object.assign({}, artwork);
    newArtwork.redirectLaunchV3 = redirectLaunchV3;
    newArtwork.modified = Date.now();
    apolloClient.mutate({
      mutation: UpdateArtwork,
      variables: {
        input: {
          id: artworkId,
          redirectLaunchV3,
          modified: newArtwork.modified,
        },
      },
      update: () => {
        store.commit('UPDATE_ARTWORK', { artwork: newArtwork });
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateArtwork: {
          __typename: 'Artwork',
          id: artworkId,
          status: newArtwork.status || null,
          creatorVersion: newArtwork.creatorVersion || null,
          animationHasAlpha: typeof newArtwork.animationHasAlpha === 'boolean' ? newArtwork.animationHasAlpha : null,
          animationVideoOrientation: newArtwork.animationVideoOrientation || null,
          redirectLaunchV3: !!redirectLaunchV3,
          modified: newArtwork.modified,
        },
      },
    })
      .then(() => {
        resolve(redirectLaunchV3);
      })
      .catch(error => {
        console.error(error);
        store.commit('UPDATE_ARTWORK', { artwork: oldArtwork });
        reject(error);
      });
  });
}

const ArtworkServices = {
  updateArtwork,
  updateArtworkLaunchMode,
};

export default ArtworkServices;
